import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronDown } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { sectionOf } from './nav'

// 본문 맨 위에 항상 있는 경로와 제목. "지금 무엇을 보고 있는가" 를 화면이 먼저 말한다.

export type Step = {
  label: string
  to?: string
  /** 같은 층의 다른 항목. 있으면 이 단계에 마우스를 올렸을 때 펼쳐져 바로 옮겨 간다. */
  siblings?: { label: string; to: string }[]
}

export function Crumb({ steps }: { steps: Step[] }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
      {steps.map((s, i) => (
        <span key={i} className="contents">
          {i > 0 && <span className="opacity-40">›</span>}
          {s.siblings && s.siblings.length > 0 ? (
            <CrumbSwitch step={s} current={i === steps.length - 1} />
          ) : s.to ? (
            <Link to={s.to} className="hover:text-foreground hover:underline">
              {s.label}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{s.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

// 마우스를 올리면 형제 목록이 열리고, 벗어나면 닫힌다. 단계 글자 자체는 여전히 링크다.
function CrumbSwitch({ step, current }: { step: Step; current: boolean }) {
  const [open, setOpen] = useState(false)
  const label = step.to ? (
    <Link to={step.to} className={current ? 'font-medium text-foreground' : 'hover:text-foreground hover:underline'}>
      {step.label}
    </Link>
  ) : (
    <span className="font-medium text-foreground">{step.label}</span>
  )
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild onMouseEnter={() => setOpen(true)}>
        {/* 손잡이는 버튼이다 — 마우스를 올리는 것만으로 여는 것은 터치에 없다. */}
        <button type="button" className="inline-flex items-center gap-0.5" aria-label={`${step.label} 바꾸기`}>
          {label}
          <ChevronDown className="size-3 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onMouseLeave={() => setOpen(false)}>
        {step.siblings!.map((sib) => (
          <DropdownMenuItem key={sib.to} asChild>
            <Link to={sib.to}>{sib.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SectionCrumb() {
  const { pathname } = useLocation()
  return <Crumb steps={[{ label: sectionOf(pathname).label }]} />
}

export function PageHead({
  icon,
  kind,
  title,
  afterTitle,
  facts,
  right,
}: {
  icon?: ReactNode
  kind?: string | undefined
  /** 제목. 상세 페이지는 그 자리에서 고치는 칸(`InlineEdit`)을 둘 수 있다. */
  title: ReactNode
  /** 제목 바로 오른쪽 — 그 화면의 목록을 좁히는 것만 둔다. 조작은 `right` 다. */
  afterTitle?: ReactNode
  facts?: ReactNode
  right?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start gap-3 pt-4">
      {icon ? <div className="mt-0.5 text-brand-hover">{icon}</div> : null}
      {/* 좁은 화면에서는 제목이 한 줄을 차지하고 조작이 아랫줄로 내려간다. */}
      <div className="min-w-0 flex-1 max-sm:basis-full">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="truncate text-[19px] font-semibold tracking-tight">{title}</h1>
          {kind ? (
            <span className="rounded border px-1.5 py-px text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
              {kind}
            </span>
          ) : null}
          {afterTitle}
        </div>
        {facts ? <div className="mt-1 text-[12px] text-muted-foreground">{facts}</div> : null}
      </div>
      {right}
    </div>
  )
}

// right 는 제목 줄 오른쪽의 조작 칩 자리다. 절이 가진 조작이 하나뿐일 때 쓴다.
// lead 는 절 제목 왼쪽 — 그 절의 줄을 통째로 고르는 체크박스 자리다.
export function Section({ title, lead, right, children }: { title: string; lead?: ReactNode; right?: ReactNode; children: ReactNode }) {
  const heading = <h2 className="text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{title}</h2>
  return (
    <section className="pt-6">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        {lead ? (
          <div className="flex items-center gap-2">
            {lead}
            {heading}
          </div>
        ) : (
          heading
        )}
        {right}
      </div>
      {children}
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed py-8 text-[12px] text-muted-foreground">
      {children}
    </div>
  )
}
